<template lang="pug">
.tree-item.action-on-hover
    .tree-node(
        @click.stop="handleClickOnItem"
    )
        //- ICON
        .node-icon
            .node-icon__default(v-if="!hasChildren")
            svg-icon(
                v-else
                name='folder' size="small"
                :class="open ? 'node-icon__open' : 'node-icon__close'"
                @click.native.stop="open = !open"
            )

        //- INFO
        .tree-node__info
            //- TOP: ID & FREQUENCY
            .l-flex-row.tree-node__top-info(v-if="showTop")
                //- ID
                .tree-node__id(v-if="showId") {{ criterion.id }}
                //- FREQUENCY
                svg-icon.tree-node__smartsegment(
                    v-if="criterion.hasFrequency && isNotSharedSegment"
                    name='period'
                    size='small'
                )

            //- MIDDLE: LABEL
            .tree-node__label(:title="criterion.label") {{ criterion.label }}

            //- BOTTOM: PATH & TAGS
            .tree-node__btm-info(v-if="showBottom")
                //- TAGS
                .criterion-taglist(v-if="criterion.hasTags")
                    .criterion-tag(
                        v-for="(tag, index) of tags"
                        @click.stop="$emit('onTagClick', tag.label)"
                        :key="index"
                    )
                        span.tag-label(:title="tag.label") {{ tag.label }}
                    button.btn-toogle-tag.btn-nude(
                        v-if="criterion.tags.length > 3"
                        @click.stop="tagExpanded = !tagExpanded"
                    ) {{ tagExpanded ? 'less' : 'more' }}
                //- PATH
                .criterion-path(v-if="showPath") {{ path }}

        //- ACTION
        .tree-node__action(v-if="showActions")
            //- EDIT
            .tree-node__action(
                :title="editTitle"
                @click.stop="handleEdit"
            )
                svg-icon(
                    name='edit'
                    size="medium"
                    :disabled="!editable"
                )

            //- ADD IN FORMULA
            .tree-node__action(
                title="Add audience to formula"
                @click.stop="addCriterion()"
            )
                svg-icon(name="shareFromSquare" size="large")

        .tree-node__action.action-hover-reveal
            .tree-node__action(
                v-if="criterion.children.length > 0 && criterion.selectable && criterion.type === 'firstParty'"
                :title="'Add children'"
                @click.stop="addChildren"
            )
                svg-icon(
                    name='list'
                    size="medium"
                )


    transitionGroup(
        v-if="open && hasChildren"
        tag="div"
        name="slide-fade"
    )
        CriterionItem(
            v-for="(criterionItem, i) of criterionChildren(criterion.children)"
            :criterion="criterionItem"
            :formula="formula"
            :key="i"
            class="children-node"
        )

</template>

<script>
import { mapMutations, mapState } from 'vuex'
import GlobalDialog from '@/components/common/GlobalDialog'
import { create } from 'vue-modal-dialogs'
import { CustomSegment } from '@node/wam-audience-builder'

// Create the dialog function with no other arguments to
// tell vue-moda-dialogs to treat the first argument as the data object
const confirm = create(GlobalDialog)

export default {
    name: 'CriterionItem',

    props: {
        criterion: {
            type: Object,
            required: true
        },
        displayPath: {
            type: Boolean,
            default: false
        },
        formula: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            tagExpanded: false,
            open: false
        }
    },

    computed: {
        ...mapState('data/settings', {
            surfIntensity: state => state.general.surfintensity,
            quantile: state => state.general.quantile,
            frequency: state => state.general.frequency
        }),
        path() {
            if (this.criterion.type === 'thirdParty' && this.criterion.parent) {
                return '↳ ' + this.criterion.path.slice(1).join(' // ') + ' // ' + this.criterion.parent.label
            }
            return '↳ ' + this.criterion.path.slice(1).join(' // ')
        },
        ...mapState('ui/builder', {
            criteriaSelected: state => state.formula.criteriaSelected,
            pathSelected: state => state.formula.path
        }),

        showBottom() {
            return this.showPath || this.criterion.hasTags
        },

        showId() {
            return this.criterion.displayId
        },

        showPath() {
            // TODO: Change this when other criterion type need to be true
            return this.displayPath
        },

        showTop() {
            return this.showId || this.criterion.hasFrequency
        },

        showActions() {
            return this.criterion.category == 'Audience' && !this.criterion.isShared
        },

        tags() {
            if (this.tagExpanded) {
                return this.criterion.tags
            }

            return this.criterion.tags.slice(0, 3)
        },

        hasChildren() {
            return this.criterion.children.length > 0 || this.criterion.hasChildren
        },

        isNotSharedSegment() {
            return this.criterion.category !== 'SharedSegment'
        },

        editable() {
            return this.criterion.category == 'Audience' && this.criterion.subCategory !== 'Cclones'
        },

        editTitle() {
            return this.editable ? 'Edit this audience' : 'This audience can not be edited'
        },

        thirdPartyTypeInGenericFormula() {
            const { genericFormula } = this.criterion
            // genericFormula have 2 types : array or object
            if (genericFormula && genericFormula.length > 0) {
                // Segment is behavioral genericFormula type
                return genericFormula.some(formula => formula?.type === 'Sociodemo' || formula?.type === 'Segment')
            }
            return genericFormula.type === 'Sociodemo' || genericFormula.type === 'Segment'
        }
    },

    methods: {
        ...mapMutations('builder', ['setAudienceInEditionId']),
        ...mapMutations('ui/builder', ['setDisplayModal', 'openCriteria']),
        ...mapMutations('notifications', ['setAddedNotification']),

        handleClickOnItem() {
            if (!this.criterion.selectable) {
                this.open = !this.open
                return
            }
            this.addCriterion()
        },

        addCriterion() {
            if (this.criterion.category === 'CustomSegment') {
                this.formula.addCriterion(new CustomSegment(this.criterion))
            } else {
                this.formula.addCriterion(this.criterion)
            }
        },

        async handleEdit() {
            if (!this.editable) return

            const editConfirmation =
                this.formula.isEmpty ||
                (await confirm({
                    content: 'The formula in the builder will be cleared to edit the audience\'s formula',
                    yes: 'Continue',
                    no: 'Cancel'
                }))

            if (editConfirmation) {
                const socioDemoList = {}
                Object.entries(this.$store.state.data.demographic.collection ?? {}).forEach(entry => {
                    const [key, value] = entry // eslint-disable-line no-unused-vars
                    socioDemoList[value.id] = value.subCategory
                })
                this.setAudienceInEditionId(this.criterion.crossId)
                this.formula.initializeFromFormula(this.criterion.genericFormula, socioDemoList, this.frequency).catch(err => {
                    this.setAudienceInEditionId(null)
                    this.setDisplayModal({ item: 'formulaHasOnlyDeletedCriteria', display: true })
                    throw err
                })
                const hasFrequencySegmentsAndDisable = this.formula.hasFrequencyInFormula && !this.frequency
                if (
                    hasFrequencySegmentsAndDisable ||
                    (
                        this.thirdPartyTypeInGenericFormula &&
                        (
                            !this.surfIntensity ||
                            !this.quantile
                        )
                    )
                ) {
                    this.setAddedNotification({
                        type: 'warning',
                        message: 'The formula of the audience has been altered because some data or feature are unavailable'
                    })
                }
            }
        },

        addChildren() {
            this.criterion.children.forEach(item => {
                this.formula.addCriterion(item)
            })
        },

        criterionChildren(children) {
            return [...children].sort((a, b) => a.label.localeCompare(b.label))
        },
    },
    watch: {
        criteriaSelected: {
            handler(val) {
                if (val === this.criterion.label) {
                    const index = this.pathSelected.findIndex(path => path === val)
                    if (index < this.pathSelected.length - 1) {
                        // we don't any other solution so we wait a moment until the state criteria selected is modified
                        setTimeout(() => {
                            this.openCriteria(this.pathSelected[index + 1])
                        }, 0)
                    }
                    this.open = true
                }
            }
        }
    }
}
</script>
